import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    Button,
    Card,
    Text,
    Heading,
    Container,
    Icon,
    Badge
} from '../components/ui/index.jsx';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export default function Contact() {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [formData, setFormData] = useState({
        subject: '',
        message: '',
        category: 'general-feedback'
    });
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success'|'error', message: string }
    const [remaining, setRemaining] = useState(5);
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);

    // Load Turnstile script
    useEffect(() => {
        if (typeof window.turnstile !== 'undefined') return;

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    // Initialize Turnstile widget
    useEffect(() => {
        if (!turnstileRef.current || !isAuthenticated) {
            return;
        }

        if (!TURNSTILE_SITE_KEY || typeof TURNSTILE_SITE_KEY !== 'string') {
            return;
        }

        const initTurnstile = () => {
            if (typeof window.turnstile === 'undefined') {
                setTimeout(initTurnstile, 100);
                return;
            }

            // Clear any existing widget
            if (widgetIdRef.current !== null) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {
                    // Silently ignore cleanup errors
                }
            }

            // Render new widget
            try {
                widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => {
                        setTurnstileToken(token);
                    },
                    'error-callback': (errorCode) => {
                        setTurnstileToken(null);
                    },
                    'expired-callback': () => {
                        setTurnstileToken(null);
                    },
                    theme: 'dark'
                });
            } catch (error) {
                // Silently handle render errors
            }
        };

        initTurnstile();

        return () => {
            if (widgetIdRef.current !== null) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {
                    // Silently ignore cleanup errors
                }
                widgetIdRef.current = null;
            }
        };
    }, [isAuthenticated]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated) {
            navigate('/signin');
            return;
        }

        if (!turnstileToken) {
            setSubmitStatus({
                type: 'error',
                message: 'Please complete the CAPTCHA verification'
            });
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch('/api/v2/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    ...formData,
                    turnstileToken
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus({
                    type: 'success',
                    message: data.message || 'Your message has been sent successfully!'
                });
                setRemaining(data.remaining || 0);

                // Reset form
                setFormData({
                    subject: '',
                    message: '',
                    category: 'general-feedback'
                });
                setTurnstileToken(null);

                // Reset Turnstile widget
                if (widgetIdRef.current !== null && typeof window.turnstile !== 'undefined') {
                    try {
                        window.turnstile.reset(widgetIdRef.current);
                    } catch (e) {
                        // Silently handle reset errors
                    }
                }

                // Scroll to success message
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                setSubmitStatus({
                    type: 'error',
                    message: data.error || 'Failed to send message. Please try again.'
                });
            }
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: 'An error occurred. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid =
        formData.subject.trim().length >= 3 &&
        formData.message.trim().length >= 10 &&
        turnstileToken !== null;

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1 flex flex-col justify-center">
                    <Container size="5xl">
                        {/* Hero Section - Added to match HowItWorks style */}
                        <div className="py-12 md:py-16 text-center px-4 relative">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-[#7B61FF]/10 to-[#FF00FF]/10 blur-[80px] rounded-full pointer-events-none"></div>

                            <Heading level={1} className="mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                                Contact Us
                            </Heading>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mb-8"></div>

                            <Text variant="secondary" size="lg" className="max-w-2xl mx-auto leading-relaxed text-gray-300 mb-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                                Your insights are the blueprints for our evolution. Whether it's a bug, a breakthrough idea, or just a thought, we want to hear it.
                            </Text>
                            <Text variant="secondary" size="sm" className="text-gray-400 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                                Or email us directly: <a href="mailto:support@theaiarchitect.ai" className="text-[#7B61FF] hover:text-[#BA87FF] transition-colors">support@theaiarchitect.ai</a>
                            </Text>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-start mb-12 md:mb-20">

                            {/* Left Column: Context & Expectations */}
                            <div className="space-y-8 lg:sticky lg:top-32 order-2 lg:order-1 hidden lg:block">
                                <Heading level={4} className="text-gray-400 uppercase tracking-widest text-sm font-semibold mb-6"> Our Commitment </Heading>

                                {/* Feature/Expectations List */}
                                <div className="space-y-6">
                                    <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                                            <Icon name="lightning" size="sm" className="text-blue-400" />
                                        </div>
                                        <div>
                                            <Heading level={4} className="text-white text-base mb-1">Direct Engineering Channel</Heading>
                                            <Text variant="secondary" size="sm">Your feedback bypasses support and goes directly to the core engineering team for review.</Text>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                                            <Icon name="trendingUp" size="sm" className="text-purple-400" />
                                        </div>
                                        <div>
                                            <Heading level={4} className="text-white text-base mb-1">Direct Impact</Heading>
                                            <Text variant="secondary" size="sm">Your feedback directly influences our development roadmap and feature prioritization.</Text>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20">
                                            <Icon name="shield" size="sm" className="text-green-400" />
                                        </div>
                                        <div>
                                            <Heading level={4} className="text-white text-base mb-1">Spam Protection</Heading>
                                            <Text variant="secondary" size="sm">Limited to 5 submissions per day to ensure high-quality attention to each message.</Text>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Feedback Form */}
                            <div className="relative order-1 lg:order-2">
                                {/* Glow effect behind form */}
                                <div className="absolute -inset-1 bg-gradient-to-b from-[#7B61FF]/20 to-transparent rounded-3xl blur-xl opacity-50 pointer-events-none"></div>

                                <div className="glass-panel p-4 md:p-8 rounded-2xl border-[#7B61FF]/20 bg-[#0F1117]/80 backdrop-blur-xl relative shadow-2xl">
                                    {!isAuthenticated ? (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                                                <Icon name="lock" size="lg" className="text-gray-400" />
                                            </div>
                                            <Heading level={3} className="mb-3 text-white">Join the Conversation</Heading>
                                            <Text variant="secondary" className="mb-8 max-w-sm mx-auto">
                                                Please sign in to share your feedback. It helps us follow up with you directly.
                                            </Text>
                                            <Button
                                                onClick={() => navigate('/signin')}
                                                className="bg-[#7B61FF] hover:bg-[#6B52E7] text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg shadow-[#7B61FF]/25 w-full sm:w-auto"
                                            >
                                                Sign In to Contribute
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            {/* User Status Bar */}
                                            <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#7B61FF] to-[#BA87FF] flex items-center justify-center text-xs font-bold text-white">
                                                        {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="text-sm">
                                                        <div className="text-white font-medium">{user?.displayName || 'Architect User'}</div>
                                                        <div className="text-gray-500 text-xs">{user?.email}</div>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className="border-white/10 text-gray-400 text-xs">
                                                    {remaining} left today
                                                </Badge>
                                            </div>

                                            {submitStatus && (
                                                <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 ${submitStatus.type === 'success'
                                                    ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                                    : 'bg-red-500/10 border-red-500/20 text-red-400'
                                                    }`}>
                                                    <Icon name={submitStatus.type === 'success' ? 'checkCircle' : 'alertTriangle'} size="sm" className="mt-0.5" />
                                                    <div className="text-sm font-medium">{submitStatus.message}</div>
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit} className="space-y-6">
                                                {/* Category Selection - Grid */}
                                                <div className="space-y-3">
                                                    <label className="text-sm font-medium text-gray-300">What's on your mind?</label>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {[
                                                            { id: 'general-feedback', label: 'Thoughts', icon: 'mail' },
                                                            { id: 'bug-report', label: 'Bug Report', icon: 'alertTriangle' }, // Using available icon
                                                            { id: 'feature-request', label: 'Feature Idea', icon: 'lightbulb' },
                                                            { id: 'other-help', label: 'Other / Help', icon: 'question' }
                                                        ].map((item) => (
                                                            <button
                                                                type="button"
                                                                key={item.id}
                                                                onClick={() => setFormData(prev => ({ ...prev, category: item.id }))}
                                                                className={`
                                                                    flex flex-col items-center justify-center p-4 rounded-xl border text-sm font-medium transition-all duration-200
                                                                    ${formData.category === item.id
                                                                        ? 'bg-[#7B61FF]/20 border-[#7B61FF] text-white shadow-[0_0_15px_rgba(123,97,255,0.15)]'
                                                                        : 'bg-white/5 border-white/5 text-gray-400 hover:bg-white/10 hover:border-white/10 hover:text-white'
                                                                    }
                                                                `}
                                                            >
                                                                <Icon name={item.icon} size="sm" className={`mb-2 ${formData.category === item.id ? 'text-[#BA87FF]' : 'opacity-70'}`} />
                                                                {item.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Subject */}
                                                <div className="space-y-2">
                                                    <label htmlFor="subject" className="text-sm font-medium text-gray-300">Subject</label>
                                                    <input
                                                        type="text"
                                                        id="subject"
                                                        name="subject"
                                                        value={formData.subject}
                                                        onChange={handleInputChange}
                                                        maxLength={100}
                                                        placeholder="Brief summary..."
                                                        className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#7B61FF] focus:ring-1 focus:ring-[#7B61FF] transition-all"
                                                        required
                                                    />
                                                </div>

                                                {/* Message */}
                                                <div className="space-y-2">
                                                    <label htmlFor="message" className="text-sm font-medium text-gray-300">Details</label>
                                                    <textarea
                                                        id="message"
                                                        name="message"
                                                        value={formData.message}
                                                        onChange={handleInputChange}
                                                        maxLength={2000}
                                                        rows={6}
                                                        placeholder="Tell us everything..."
                                                        className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#7B61FF] focus:ring-1 focus:ring-[#7B61FF] transition-all resize-none"
                                                        required
                                                    />
                                                    <div className="text-right text-xs text-gray-500">
                                                        {formData.message.length}/2000
                                                    </div>
                                                </div>

                                                {/* Turnstile */}
                                                <div className="pt-2">
                                                    <div ref={turnstileRef} className="cf-turnstile"></div>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex items-center gap-4 pt-2">
                                                    <Button
                                                        type="submit"
                                                        disabled={!isFormValid || isSubmitting}
                                                        className={`
                                                            flex-1 py-3.5 rounded-xl font-bold text-sm uppercase tracking-wide transition-all
                                                            ${!isFormValid || isSubmitting
                                                                ? 'bg-white/5 text-gray-500 cursor-not-allowed border border-white/5'
                                                                : 'bg-[#7B61FF] hover:bg-[#6B52E7] text-white shadow-[0_0_20px_rgba(123,97,255,0.4)] hover:shadow-[0_0_30px_rgba(123,97,255,0.6)] hover:scale-[1.02]'
                                                            }
                                                        `}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="flex items-center justify-center gap-2">
                                                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                                Sending...
                                                            </span>
                                                        ) : 'Send Feedback'}
                                                    </Button>
                                                </div>
                                            </form>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
}
